import { getUserDetails, isAdminUser } from "../../services/authService";
import { isCheckoutPackageFlow } from "../checkout/checkoutFlowType";

const ENV_KEY = "VITE_HR_DP_DYNAMIC_MAINTENANCE";
export const HR_DP_MAINTENANCE_LS_KEY = "HR_DP_DYNAMIC_MAINTENANCE";

export function truthyMaintenanceToken(value) {
  const v = String(value || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

/** Raw env toggle (staging). */
export function getHrDpMaintenanceEnvRaw() {
  try {
    return String(import.meta.env?.[ENV_KEY] ?? "").trim();
  } catch {
    return "";
  }
}

function readLocalHrDpMaintenanceFlag() {
  try {
    return String(localStorage.getItem(HR_DP_MAINTENANCE_LS_KEY) ?? "").trim();
  } catch {
    return "";
  }
}

/**
 * Maintenance mode enabled (env or localStorage) — unrelated to admin bypass.
 */
export function isDynamicPackagesMaintenanceModeEnabled() {
  if (truthyMaintenanceToken(getHrDpMaintenanceEnvRaw())) return true;
  return truthyMaintenanceToken(readLocalHrDpMaintenanceFlag());
}

/** admin / super_admin / is_admin-equivalent flags (see authService). */
export function isDynamicPackagesAdminBypass() {
  return isAdminUser();
}

/**
 * G.4E — Dynamic Package removed from active product for all users.
 * Staging maintenance flag / admin bypass retained for dormant code paths only.
 */
export function isDynamicPackageProductActive() {
  return false;
}

/**
 * Central kill-switch: product inactive (G.4E), or staging flag ON for non-admins.
 */
export function isDynamicPackagesTemporarilyDisabled() {
  if (!isDynamicPackageProductActive()) return true;
  return (
    isDynamicPackagesMaintenanceModeEnabled() && !isDynamicPackagesAdminBypass()
  );
}

export function resolveDynamicPackagesUserRoleForTrace() {
  try {
    const u = typeof getUserDetails === "function" ? getUserDetails() : null;
    if (!u || typeof u !== "object") return "guest_or_unknown";

    const role = String(u.role ?? u.agentRole ?? "")
      .trim()
      .toLowerCase();
    const ut = String(u.usertype ?? u.userType ?? "")
      .trim()
      .toLowerCase();

    const adminish =
      Boolean(u.is_admin ?? u.isAdmin ?? u.admin) ||
      ut === "admin" ||
      ut === "super_admin" ||
      role === "admin" ||
      role === "super_admin";
    if (adminish && (role || ut)) return `${role || "norole"}:${ut || "notype"}`;
    if (role) return role;
    if (ut) return ut;
    return "authenticated_unknown_shape";
  } catch {
    return "guest_or_unknown";
  }
}

/**
 * Stable console trace for gated flows when maintenance env is enabled.
 */
export function reportHrDpTempDisabledTrace({
  userRole,
  route,
  gateTriggered,
  bypassAdmin,
  blockedReason,
}) {
  if (isDynamicPackageProductActive() && !isDynamicPackagesMaintenanceModeEnabled()) {
    return;
  }

  try {
    const payload = {
      ts: new Date().toISOString(),
      userRole: userRole ?? resolveDynamicPackagesUserRoleForTrace(),
      route: String(route ?? ""),
      gateTriggered: String(gateTriggered ?? ""),
      bypassAdmin: Boolean(bypassAdmin),
      blockedReason: String(blockedReason ?? ""),
    };
    // eslint-disable-next-line no-console
    console.info("[HR_DP_TEMP_DISABLED_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

/**
 * URL prefixes that Route-level gates should intercept (excluding /hotel/offer — handled inside PriceOffer).
 */
export function pathnameMatchesHrDpRouteMaintenanceSurfaces(pathname = "") {
  const p = String(pathname || "");
  if (!p) return false;
  if (p.includes("/dynamic-package-profile/")) return true;
  if (p.includes("/offer/dynamic-package/")) return true;
  if (p.includes("/dynamic/offer")) return true;
  if (p.includes("/dynamic/profile")) return true;
  if (p.includes("/dynamic/success")) return true;
  if (/\/hotelprofile\/[^/]+\/dynamic\//.test(p)) return true;
  if (p.includes("/flights/offer/")) return true;
  if (p.includes("/p/dashboard/dynamic/profile/")) return true;
  if (p.includes("/p/dashboard/dynamic/success/")) return true;
  if (/\/p\/dashboard\/packages\/proposal\//.test(p)) return true;
  // G.4E legacy entry points
  if (p.includes("/p/dashboard/searchdynamic")) return true;
  if (/\/p\/dashboard\/orders\/dynamic-packages(\/|$)/.test(p)) return true;
  return false;
}

/**
 * Non-dashboard guest URLs that PriceOffer renders for HR/DP (excludes `/hotel/offer/*`;
 * hotel/offer is gated separately when dynamic order hydration indicates DP).
 */
export function shouldBlockHrDpPriceOfferMaintenanceNonDashboardPath(pathname = "") {
  const p = String(pathname || "");
  if (!p || p.startsWith("/p/dashboard")) return false;
  return pathnameMatchesHrDpRouteMaintenanceSurfaces(p);
}

/**
 * Route middleware helper: gate when staging flag ON, path matches DP surface, non-admin.
 */
export function evaluateHrDpRouteMaintenanceDecision(pathname = "", gateTriggered = "") {
  const route = String(pathname || "");
  const bypassAdmin = isDynamicPackagesAdminBypass();

  // G.4E — product inactive: block all Dynamic surfaces (no admin bypass).
  if (!isDynamicPackageProductActive()) {
    if (!pathnameMatchesHrDpRouteMaintenanceSurfaces(route)) {
      return { blocked: false, bypassAdmin };
    }
    reportHrDpTempDisabledTrace({
      userRole: resolveDynamicPackagesUserRoleForTrace(),
      route,
      gateTriggered,
      bypassAdmin: false,
      blockedReason: "product_not_active_g4e",
    });
    return { blocked: true, bypassAdmin: false };
  }

  const mode = isDynamicPackagesMaintenanceModeEnabled();
  if (!mode) {
    return { blocked: false, bypassAdmin };
  }
  if (!pathnameMatchesHrDpRouteMaintenanceSurfaces(route)) {
    return { blocked: false, bypassAdmin };
  }

  if (bypassAdmin) {
    return { blocked: false, bypassAdmin: true };
  }

  const userRole = resolveDynamicPackagesUserRoleForTrace();
  reportHrDpTempDisabledTrace({
    userRole,
    route,
    gateTriggered,
    bypassAdmin: false,
    blockedReason: "maintenance_blocked_non_admin",
  });
  return { blocked: true, bypassAdmin: false };
}

/**
 * PaymentAgent: dynamic / package checkout only (avoid blocking plain hotel delayed-pay IDs).
 */
export function evaluateHrDpPaymentAgentMaintenanceBlock({
  pathname = "",
  dynamicOrderId,
  flightOrderId,
  searchTab,
  savedDeal,
  gateTriggered = "payment_agent",
}) {
  const route = String(pathname || "");
  const bypassAdmin = isDynamicPackagesAdminBypass();

  const routeHasDynamicProfile =
    /\/dynamic\/profile\//.test(route) ||
    /\/p\/dashboard\/dynamic\/profile\//.test(route);

  const paramsIndicatePackageOrder =
    Boolean(dynamicOrderId) || Boolean(flightOrderId);

  const paymentPathLooksLikePackageFlow =
    route.includes("/p/dashboard/payment/") &&
    isCheckoutPackageFlow({
      dynamicOrderId,
      flightOrderId,
      searchTab,
      savedDeal,
    });

  const shouldBlock =
    paramsIndicatePackageOrder ||
    routeHasDynamicProfile ||
    paymentPathLooksLikePackageFlow;

  if (!shouldBlock) {
    return { blocked: false, bypassAdmin };
  }

  // G.4E — product inactive: block package payment surfaces for everyone.
  if (!isDynamicPackageProductActive()) {
    reportHrDpTempDisabledTrace({
      userRole: resolveDynamicPackagesUserRoleForTrace(),
      route,
      gateTriggered,
      bypassAdmin: false,
      blockedReason: "product_not_active_g4e_payment",
    });
    return { blocked: true, bypassAdmin: false };
  }

  const mode = isDynamicPackagesMaintenanceModeEnabled();
  if (!mode) return { blocked: false, bypassAdmin };

  if (bypassAdmin) {
    return { blocked: false, bypassAdmin: true };
  }

  const userRole = resolveDynamicPackagesUserRoleForTrace();
  reportHrDpTempDisabledTrace({
    userRole,
    route,
    gateTriggered,
    bypassAdmin: false,
    blockedReason: "maintenance_blocked_non_admin_payment",
  });
  return { blocked: true, bypassAdmin: false };
}
